import { Controller } from '@hotwired/stimulus'
import { getCount } from '../../condition_search/api/common/count'

export default class extends Controller {
  static targets = ['count', 'areaValue']

  connect () {
    this.updateCount()

    // ConditionSearchフォームの変更を監視してカウントを更新
    const conditionSearchForm = this._getConditionSearchForm()
    if (conditionSearchForm) {
      this._formChangeHandler = () => this.updateCount()
      conditionSearchForm.addEventListener('change', this._formChangeHandler)
    }

    // モーダルが閉じられたとき、フォームの現在状態をサイドバーのUI要素に反映するためのイベントリスナーを登録する
    this._modalClosedHandler = (e) => this._syncFromModal(e)
    window.addEventListener('condition-search-modal-closed', this._modalClosedHandler)
  }

  disconnect () {
    const conditionSearchForm = this._getConditionSearchForm()
    if (conditionSearchForm && this._formChangeHandler) {
      conditionSearchForm.removeEventListener('change', this._formChangeHandler)
    }
    if (this._modalClosedHandler) {
      window.removeEventListener('condition-search-modal-closed', this._modalClosedHandler)
    }
  }

  // エリアモーダルを開く
  openAreaModal (e) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('open-condition-search-modal', { detail: { modalIndex: 0 } }))
  }

  // 介護度/医療モーダルを開く
  openCareLevelModal (e) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('open-condition-search-modal', { detail: { modalIndex: 2 } }))
  }

  // 詳細設定モーダルを開く
  openDetailModal (e) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('open-condition-search-modal', { detail: { modalIndex: 3 } }))
  }

  // 料金セレクト変更をConditionSearchフォームに同期
  syncFeeField (e) {
    const field = e.currentTarget.dataset.feeField
    const value = e.currentTarget.value
    const form = this._getConditionSearchForm()
    if (!form || !field) return

    const formInput = form.querySelector(`[name="${field}"]`)
    if (formInput) {
      formInput.value = value
      formInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // 生活保護チェックボックスをConditionSearchフォームに同期
  syncWelfareCheckbox (e) {
    const checked = e.currentTarget.checked
    const form = this._getConditionSearchForm()
    if (!form) return

    const formCheckbox = form.querySelector('[name="welfare_acceptable"]')
    if (formCheckbox) {
      formCheckbox.checked = checked
      formCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // 通常チェックボックス（介護度・施設種別）をConditionSearchフォームに同期
  syncCheckbox (e) {
    const name = e.currentTarget.dataset.checkboxName
    const value = e.currentTarget.dataset.checkboxValue
    const checked = e.currentTarget.checked
    const form = this._getConditionSearchForm()
    if (!form || !name || !value) return

    const formCheckbox = form.querySelector(`[name="${name}"][value="${value}"]`)
    if (formCheckbox) {
      formCheckbox.checked = checked
      formCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // 医療処置チェックボックス（認知症など複数IDを一括同期）をConditionSearchフォームに同期
  // 複数IDを一括でセットしてから最後に1回だけchangeイベントを発火し、API呼び出しを最小化
  syncMedicalCareCheckbox (e) {
    if (!e.currentTarget.dataset.medicalCareIds) return
    const medicalCareIds = e.currentTarget.dataset.medicalCareIds.split(',')
    const checked = e.currentTarget.checked
    const form = this._getConditionSearchForm()
    if (!form) return

    let lastFormCheckbox = null
    medicalCareIds.forEach(id => {
      const formCheckbox = form.querySelector(`[name="medical_care_ids[]"][value="${id}"]`)
      if (formCheckbox) {
        formCheckbox.checked = checked
        lastFormCheckbox = formCheckbox
      }
    })
    // 全チェックボックスのセット完了後、1回だけchangeイベントを発火
    if (lastFormCheckbox) {
      lastFormCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // こだわり条件チェックボックス（複数IDを一括同期）をConditionSearchフォームに同期
  // 複数IDを一括でセットしてから最後に1回だけchangeイベントを発火し、API呼び出しを最小化
  syncFeatureCheckbox (e) {
    if (!e.currentTarget.dataset.featureIds) return
    const featureIds = e.currentTarget.dataset.featureIds.split(',')
    const checked = e.currentTarget.checked
    const form = this._getConditionSearchForm()
    if (!form) return

    let lastFormCheckbox = null
    featureIds.forEach(id => {
      const formCheckbox = form.querySelector(`[name="feature_content_ids[]"][value="${id}"]`)
      if (formCheckbox) {
        formCheckbox.checked = checked
        lastFormCheckbox = formCheckbox
      }
    })
    // 全チェックボックスのセット完了後、1回だけchangeイベントを発火
    if (lastFormCheckbox) {
      lastFormCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // 検索ボタンのクリック → ConditionSearchのvalidateを経由してフォーム送信
  submitSearch (e) {
    e.preventDefault()
    // モーダル内の「検索する」ボタン（validate アクション付き）をプログラム的にクリックする
    // これによりConditionSearchコントローラーのvalidate処理（静的URL生成）が動作する
    const validateButton = document.querySelector('#condition-search [data-action*="validate"]')
    if (validateButton) {
      validateButton.click()
      return
    }
    // validateボタンが見つからない場合はフォームを直接送信
    const form = this._getConditionSearchForm()
    if (form) {
      form.requestSubmit()
    }
  }

  // カウントAPIを呼び出してN件表示を更新（複数のCTAボタンを一括更新）
  updateCount () {
    const form = this._getConditionSearchForm()
    if (!form || typeof FormData !== 'function') return

    const formData = new FormData(form)

    // city_ids / ordinance_designated_city_ids / station_ids がある場合は prefecture_id を除外
    const hasCityOrStation =
      formData.getAll('city_ids[]').length > 0 ||
      formData.getAll('ordinance_designated_city_ids[]').length > 0 ||
      formData.getAll('station_ids[]').length > 0
    const prefectureId = hasCityOrStation ? null : formData.get('prefecture_id')

    const params = {
      prefecture_id: prefectureId,
      city_ids: formData.getAll('city_ids[]'),
      ordinance_designated_city_ids: formData.getAll('ordinance_designated_city_ids[]'),
      station_ids: formData.getAll('station_ids[]'),
      admission_fee_min: formData.get('admission_fee_min'),
      admission_fee_max: formData.get('admission_fee_max'),
      monthly_fee_min: formData.get('monthly_fee_min'),
      monthly_fee_max: formData.get('monthly_fee_max'),
      walking_time: formData.get('walking_time'),
      care_level_ids: formData.getAll('care_level_ids[]'),
      feature_content_ids: formData.getAll('feature_content_ids[]'),
      category_ids: formData.getAll('category_ids[]'),
      medical_care_ids: formData.getAll('medical_care_ids[]'),
      with_review: formData.get('with_review'),
      vacancies_exist: formData.get('vacancies_exist'),
      welfare_acceptable: formData.get('welfare_acceptable'),
      dementia_acceptable: formData.get('dementia_acceptable'),
      no_joint_surety_required: formData.get('no_joint_surety_required'),
    }

    getCount(params).then((count) => {
      if (!this.hasCountTarget) return
      const displayCount = count.result >= 10000 ? '10000+' : count.result
      // 上部・下部の両CTAボタンを一括更新
      this.countTargets.forEach(target => {
        target.textContent = displayCount
      })
    }).catch((err) => {
      // ページ遷移等でXHRがabortされた場合はSentryに報告しない（意図的な中断）
      // ※ axiosではタイムアウトも code === 'ECONNABORTED' になるため、message で厳密に判定する
      if (err.message === 'Request aborted') return
      // APIエラー時はカウント表示を維持する（観測性のためログは常に出力する）
      console.error('[SearchSidebar] カウント取得に失敗しました:', err)
      // ボット（bingbot等）がJSレンダリング時にWAF/CloudFrontから403を受けるケースはSentryに報告しない
      // ※カウントAPIは認証不要の公開エンドポイントのため、403はWAF/CloudFront起因とみなせる
      if (err.response?.status === 403) return
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(err, { tags: { component: 'search-sidebar-desktop', operation: 'get-count' } })
      }
    })
  }

  // ConditionSearchフォームを取得（ページ内のh1下パネルから）
  _getConditionSearchForm () {
    return document.querySelector('#condition-search form')
  }

  // モーダルが閉じたとき、フォームの現在状態をサイドバーのUI要素に反映する
  _syncFromModal (e) {
    this._syncCheckboxesFromForm()
    this._syncSelectsFromForm()
    // エリア表示の更新はエリアモーダル（index 0）が閉じられたときのみ実行する
    // 料金・詳細・介護度/医療モーダルを閉じてもエリア表示を再計算しないことで、
    // getSelectedAreaNames() の収集範囲（現在タブ + 訪問済みタブ）に依存した表示縮退を防ぐ
    if (e?.detail?.modalIndex === 0) {
      this._updateAreaDisplay(e?.detail?.areaNames ?? [], e?.detail?.prefectureName ?? '')
    }
  }

  // フォームのチェックボックス状態をサイドバーの独自チェックボックスに反映する
  _syncCheckboxesFromForm () {
    const form = this._getConditionSearchForm()
    if (!form) return

    // 通常チェックボックス（介護度・施設種別）
    this.element.querySelectorAll('[data-checkbox-name][data-checkbox-value]').forEach(cb => {
      const name = cb.dataset.checkboxName
      const value = cb.dataset.checkboxValue
      const formCb = form.querySelector(`[name="${name}"][value="${value}"]`)
      if (formCb) cb.checked = formCb.checked
    })

    // 医療処置チェックボックス（複数IDがすべてチェック済みのときにチェック状態にする）
    this.element.querySelectorAll('[data-medical-care-ids]').forEach(cb => {
      const ids = cb.dataset.medicalCareIds.split(',').filter(id => id.length > 0)
      if (ids.length === 0) return
      cb.checked = ids.every(id => {
        const formCb = form.querySelector(`[name="medical_care_ids[]"][value="${id}"]`)
        return formCb?.checked ?? false
      })
    })

    // こだわり条件チェックボックス（複数IDがすべてチェック済みのときにチェック状態にする）
    this.element.querySelectorAll('[data-feature-ids]').forEach(cb => {
      const ids = cb.dataset.featureIds.split(',').filter(id => id.length > 0)
      if (ids.length === 0) return
      cb.checked = ids.every(id => {
        const formCb = form.querySelector(`[name="feature_content_ids[]"][value="${id}"]`)
        return formCb?.checked ?? false
      })
    })
  }

  // フォームの料金セレクト値をサイドバーの独自セレクトに反映する
  _syncSelectsFromForm () {
    const form = this._getConditionSearchForm()
    if (!form) return

    ;['monthly_fee_min', 'monthly_fee_max', 'admission_fee_min', 'admission_fee_max'].forEach(field => {
      const formInput = form.querySelector(`[name="${field}"]`)
      const sidebarSelect = this.element.querySelector(`[data-fee-field="${field}"]`)
      if (formInput && sidebarSelect) sidebarSelect.value = formInput.value
    })
  }

  // エリア表示テキストをモーダルで選択された市区町村名から更新する
  // サーバーサイドの format_area_names と同じロジック（3件以内は/区切り、4件以上は末尾に...）
  _updateAreaDisplay (areaNames, prefectureName = '') {
    if (!this.hasAreaValueTarget) return

    if (areaNames.length === 0) {
      // 市区町村が全解除された場合、都道府県名にフォールバックする（都道府県も未選択なら '指定なし'）
      this.areaValueTarget.textContent = prefectureName || '指定なし'
      return
    }

    const text = areaNames.length <= 3
      ? areaNames.join('/')
      : areaNames.slice(0, 3).join('/') + '...'

    this.areaValueTarget.textContent = text
  }
}
